import { CATEGORIE_RETENTION, type Impostazioni } from '@aulaviva/shared';
import { Alert, Button, Card, TextField, useToast } from '@aulaviva/ui';
import { Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useImpostazioni, useSalvaImpostazioni } from '@/api/amministrazione';
import { formattaDataOra } from '@/lib/date';
import { amministrazione, comuni } from '@/testi';

const t = amministrazione.retention;

type Bozza = Omit<Impostazioni, 'prossimaCancellazione'>;

export function SchedaRetention() {
  const toast = useToast();
  const impostazioni = useImpostazioni();
  const salva = useSalvaImpostazioni();
  const [bozza, setBozza] = useState<Bozza | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  useEffect(() => {
    if (impostazioni.data && !bozza) {
      const { retention, webinarDefault, domande } = impostazioni.data;
      setBozza({ retention, webinarDefault, domande });
    }
  }, [impostazioni.data, bozza]);

  if (impostazioni.isPending || !bozza) return <p className="t-body text-ink-muted">{comuni.caricamento}</p>;
  if (impostazioni.isError) return <Alert variant="error" title={impostazioni.error.message} />;
  const prossima = impostazioni.data.prossimaCancellazione;

  function numero(valore: string, min: number, max: number): number {
    const n = Number(valore);
    return Number.isFinite(n) ? Math.min(max, Math.max(min, Math.round(n))) : min;
  }

  async function invia() {
    setErrore(null);
    try {
      await salva.mutateAsync(bozza!);
      toast.mostra({ variant: 'success', title: t.salvato });
    } catch (e) {
      setErrore(e instanceof Error ? e.message : t.nonValido);
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void invia();
      }}
      noValidate
      className="flex flex-col gap-6"
    >
      <p className="t-body text-ink-muted max-w-[720px]">{t.intro}</p>
      {errore && <Alert variant="error" title={errore} />}
      <Alert variant={prossima ? 'warning' : 'info'} title={prossima ? t.prossima(t.categorie[prossima.tipoDato as keyof typeof t.categorie] ?? prossima.tipoDato, formattaDataOra(prossima.quando), prossima.elementi) : t.nessunaProssima} />
      <Card className="flex flex-col gap-4">
        <h2 className="t-title-3">{amministrazione.schede.retention}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {CATEGORIE_RETENTION.map((cat) => {
            const riga = bozza.retention.find((r) => r.tipoDato === cat) ?? { tipoDato: cat, giorni: 180, descrizione: null };
            return (
              <TextField
                key={cat}
                label={t.categorie[cat]}
                type="number"
                min={30}
                max={3650}
                hint={riga.descrizione ?? t.giorniAiuto}
                meta={t.giorni}
                value={riga.giorni}
                onChange={(e) =>
                  setBozza({
                    ...bozza,
                    retention: [...bozza.retention.filter((r) => r.tipoDato !== cat), { ...riga, giorni: numero(e.target.value, 30, 3650) }],
                  })
                }
              />
            );
          })}
        </div>
      </Card>
      <Card className="flex flex-col gap-4">
        <h2 className="t-title-3">{t.defaultTitolo}</h2>
        <p className="t-body-small text-ink-muted">{t.defaultTesto}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField label={t.soglia} type="number" min={1} max={100} value={bozza.webinarDefault.sogliaPresenzaPct} onChange={(e) => setBozza({ ...bozza, webinarDefault: { ...bozza.webinarDefault, sogliaPresenzaPct: numero(e.target.value, 1, 100) } })} />
          <TextField label={t.durataTest} type="number" min={1} max={120} value={bozza.webinarDefault.testDurataMin} onChange={(e) => setBozza({ ...bozza, webinarDefault: { ...bozza.webinarDefault, testDurataMin: numero(e.target.value, 1, 120) } })} />
          <TextField label={t.finestraTest} type="number" min={1} max={72} value={bozza.webinarDefault.testFinestraOre} onChange={(e) => setBozza({ ...bozza, webinarDefault: { ...bozza.webinarDefault, testFinestraOre: numero(e.target.value, 1, 72) } })} />
          <TextField label={t.corretteMinime} type="number" min={1} max={20} value={bozza.webinarDefault.testCorretteMinime} onChange={(e) => setBozza({ ...bozza, webinarDefault: { ...bozza.webinarDefault, testCorretteMinime: numero(e.target.value, 1, 20) } })} />
        </div>
      </Card>
      <Card className="flex flex-col gap-4">
        <h2 className="t-title-3">{t.domandeTitolo}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField label={t.maxPerWebinar} type="number" min={1} max={100} value={bozza.domande.maxPerWebinar} onChange={(e) => setBozza({ ...bozza, domande: { ...bozza.domande, maxPerWebinar: numero(e.target.value, 1, 100) } })} />
          <TextField label={t.ogniSecondi} type="number" min={10} max={600} value={bozza.domande.ogniSecondi} onChange={(e) => setBozza({ ...bozza, domande: { ...bozza.domande, ogniSecondi: numero(e.target.value, 10, 600) } })} />
        </div>
      </Card>
      <Button type="submit" variant="primary" icon={Save} loading={salva.isPending} loadingText={comuni.caricamento} className="self-start">
        {t.salva}
      </Button>
    </form>
  );
}
